import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Row, Col, Card, CardHeader, CardBody, Input, FormGroup, Button, InputGroup, InputGroupText } from 'reactstrap'
import BootstrapTable from 'react-bootstrap-table-next'
import paginationFactory from 'react-bootstrap-table2-paginator';
import { api } from '../../utils';
import lodash from 'lodash';

const DomainList = () => {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [topicFilter, setTopicFilter] = useState('');
  const [minRating, setMinRating] = useState('');
  const [minTrust, setMinTrust] = useState('');
  const [minTraffic, setMinTraffic] = useState('');
  
  const fileInputRef = useRef(null);

  // Debounced fetch function to avoid spamming the API while typing
  const debouncedFetch = useCallback(
    lodash.debounce((search, topic, r_gte, t_gte, tr_gte) => {
      fetchDomains(search, topic, r_gte, t_gte, tr_gte);
    }, 500),
    []
  );

  useEffect(() => {
    debouncedFetch(searchQuery, topicFilter, minRating, minTrust, minTraffic);
  }, [searchQuery, topicFilter, minRating, minTrust, minTraffic, debouncedFetch]);

  const fetchDomains = async (search = '', topic = '', r_gte = '', t_gte = '', tr_gte = '') => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (topic) params.topic = topic;
      if (r_gte) params.rating_gte = r_gte;
      if (t_gte) params.trust_gte = t_gte;
      if (tr_gte) params.traffic_gte = tr_gte;

      const response = await api.get('link-building/domains/', { params });
      setDomains(response.data);
    } catch (error) {
      console.error('Error fetching domains:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadMessage(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('link-building/domains/upload_excel/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      setUploadMessage(`Success! Created: ${response.data.domains_created}, Updated: ${response.data.domains_updated}`);
      // Refresh the table with new data
      fetchDomains();
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadMessage(error.response?.data?.error || 'Error uploading file.');
    } finally {
      setUploading(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setTopicFilter('');
    setMinRating('');
    setMinTrust('');
    setMinTraffic('');
  };

  const priceFormatter = (cell, row) => {
    if (!row.suppliers || row.suppliers.length === 0) return '-';
    const prices = row.suppliers.map(s => parseFloat(s.price)).filter(p => !isNaN(p));
    if (prices.length === 0) return '-';
    if (prices.length === 1) return `€${prices[0]}`;
    const minPrice = Math.min(...prices);
    return `Da €${minPrice}`;
  };

  const vendorFormatter = (cell, row) => {
    if (!row.suppliers || row.suppliers.length === 0) return '-';
    const vendors = row.suppliers.map(s => s.supplier?.name).filter(Boolean);
    if (vendors.length === 0) return '-';
    return vendors.join(', ');
  };

  const columns = [
    { dataField: 'name', text: 'Domain', sort: true },
    { dataField: 'topic', text: 'Topic', sort: true },
    { dataField: 'regione', text: 'Regione', sort: true },
    { dataField: 'vendor', isDummyField: true, text: 'Vendor', formatter: vendorFormatter },
    { dataField: 'price', isDummyField: true, text: 'Prezzo', formatter: priceFormatter },
    { dataField: 'rating', text: 'Rating', sort: true },
    { dataField: 'trust', text: 'Trust', sort: true },
    { dataField: 'ip', text: 'IP', sort: true },
    { dataField: 'citation', text: 'Citation', sort: true },
    { dataField: 'traffic', text: 'Organic Traffic', sort: true },
  ]

  return (
    <div className="header bg-gradient-info pb-8 pt-5 pt-md-8">
      <div className="container-fluid">
        <Row>
          <Col>
            <Card className="shadow">
              <CardHeader className="border-0 d-flex justify-content-between align-items-center flex-wrap">
                <h3 className="mb-0">Link Building External Database</h3>
                <div className="d-flex align-items-center flex-wrap mt-3 mt-md-0">
                    <div className="mr-3 mb-2">
                       <InputGroup size="sm" className="input-group-alternative">
                        <InputGroupText>
                          <i className="fas fa-search" />
                        </InputGroupText>
                        <Input
                          type="text"
                          placeholder="Search domain..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                      </InputGroup>
                    </div>
                    
                    <div className="mr-3 mb-2">
                      <InputGroup size="sm" className="input-group-alternative">
                        <InputGroupText>
                          <i className="fas fa-filter" />
                        </InputGroupText>
                        <Input
                          type="text"
                          placeholder="Topic..."
                          value={topicFilter}
                          onChange={(e) => setTopicFilter(e.target.value)}
                        />
                      </InputGroup>
                    </div>

                    <div className="mr-3 mb-2">
                      <InputGroup size="sm" className="input-group-alternative" style={{ width: '130px' }}>
                        <InputGroupText>Rating ≥</InputGroupText>
                        <Input
                          type="number"
                          placeholder="0"
                          value={minRating}
                          onChange={(e) => setMinRating(e.target.value)}
                        />
                      </InputGroup>
                    </div>

                    <div className="mr-3 mb-2">
                      <InputGroup size="sm" className="input-group-alternative" style={{ width: '130px' }}>
                        <InputGroupText>Trust ≥</InputGroupText>
                        <Input
                          type="number"
                          placeholder="0"
                          value={minTrust}
                          onChange={(e) => setMinTrust(e.target.value)}
                        />
                      </InputGroup>
                    </div>

                    <div className="mr-3 mb-2">
                      <InputGroup size="sm" className="input-group-alternative" style={{ width: '130px' }}>
                        <InputGroupText>Traffic ≥</InputGroupText>
                        <Input
                          type="number"
                          placeholder="0"
                          value={minTraffic}
                          onChange={(e) => setMinTraffic(e.target.value)}
                        />
                      </InputGroup>
                    </div>

                    <Button color="secondary" size="sm" onClick={clearFilters} className="mr-3 mb-2" style={{ height: '31px', padding: '0 10px' }} title="Clear Filters">
                      <i className="fas fa-times" />
                    </Button>

                    <input 
                      type="file" 
                      accept=".xlsx, .xls" 
                      style={{ display: 'none' }} 
                      ref={fileInputRef}
                      onChange={handleFileUpload} 
                    />
                    
                    <Button 
                      color="primary" 
                      size="sm"
                      onClick={() => fileInputRef.current.click()}
                      disabled={uploading}
                      style={{ height: '31px', padding: '0 15px' }}
                    >
                      <i className="fas fa-upload mr-2" />
                      {uploading ? 'Uploading...' : 'Upload Excel'}
                    </Button>

                    {uploadMessage && <span className="ml-2 text-sm text-success">{uploadMessage}</span>}
                </div>
              </CardHeader>
              <CardBody>
                <div className="mb-4 text-muted text-sm px-3 py-2 border-left border-info bg-secondary" style={{ borderWidth: '4px !important' }}>
                  <i className="fas fa-info-circle mr-2 text-info" />
                  <strong>Istruzioni Excel:</strong> Caricare un file <code>.xlsx</code> o <code>.xls</code>. <br />
                  <span className="ml-4"><strong>Colonne richieste:</strong> <code>domain</code>, <code>vendor</code>, <code>prezzo</code>.</span><br />
                  <span className="ml-4"><strong>Colonne opzionali:</strong> <code>topic</code>, <code>regione</code>.</span>
                </div>
                {loading ? (
                  <p>Loading...</p>
                ) : (
                  <BootstrapTable
                    bootstrap4
                    keyField="id"
                    data={domains}
                    columns={columns}
                    pagination={paginationFactory({
                      sizePerPage: 10,
                      hideSizePerPage: false,
                    })}
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default DomainList
